import {
    ComponentRef,
    computed,
    Directive,
    effect,
    ElementRef,
    HostBinding,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewContainerRef
} from '@angular/core';
import { BaseInputComponent } from '@common/components/input/base-input/base-input.component';
import { WarningConfig, WarningDisplayComponent } from '@common/components/warning-display/warning-display.component';

/**
 * Configuration for a warning validation rule.
 * @template T The type of value being validated
 */
export interface WarningRule<T = any> {
    /** Function that returns true if the warning should be shown for the given value */
    condition: (value: T) => boolean;
    /** Translation key for the warning message */
    message: string;
    /** Severity level of the warning. Defaults to 'warning' */
    severity?: 'success' | 'warning' | 'error';
    /** Translation key for additional tooltip text */
    tooltip?: string;
}

/**
 * Directive that attaches warning validation to input components.
 * Automatically displays warning icons when validation rules fail.
 *
 * @example
 * ```html
 * <app-number-input
 *   label="LOA (m)"
 *   [(value)]="model.lengthOverall"
 *   [appWarningValidator]="vesselValidationRules.loa" />
 * ```
 */
@Directive({
    selector: '[appWarningValidator]',
    standalone: true
})
export class WarningValidatorDirective implements OnInit, OnDestroy {
    /**
     * Adds the CSS class for styling the host element as a warning validator container.
     */
    @HostBinding('class.warning-validator-host') hostClass = true;

    /**
     * Array of warning rules to validate against the input value.
     * The first matching rule will be displayed.
     */
    @Input('appWarningValidator') rules: WarningRule[] = [];

    /**
     * Position where the warning icon should appear.
     * - 'after-label': Inline with the label text (default)
     * - 'after-input': Positioned absolutely next to the input field
     */
    @Input() warningPosition: 'after-label' | 'after-input' = 'after-label';

    private warningComponentRef: ComponentRef<WarningDisplayComponent> | null = null;
    private warningWrapper: HTMLElement | null = null;

    private viewContainer = inject(ViewContainerRef);
    private hostInput = inject(BaseInputComponent<any>);
    private elementRef = inject(ElementRef);
    private renderer = inject(Renderer2);

    // Compute warning based on input value
    private warning = computed(() => {
        const value = this.hostInput.value();
        if (value === null || value === undefined) return null;

        for (const rule of this.rules) {
            if (rule.condition(value)) {
                return {
                    message: rule.message,
                    severity: rule.severity || 'warning',
                    tooltip: rule.tooltip
                } as WarningConfig;
            }
        }
        return null;
    });

    // Update warning display when warning changes
    private warningEffect = effect(() => {
        if (this.warningComponentRef) {
            this.warningComponentRef.setInput('warning', this.warning());
            this.warningComponentRef.setInput('showMessage', false);
        }
    });

    ngOnInit() {
        // Create wrapper element for positioning
        this.warningWrapper = this.renderer.createElement('div');
        this.renderer.addClass(this.warningWrapper, 'warning-validator-wrapper');

        // Create warning display component
        this.warningComponentRef = this.viewContainer.createComponent(WarningDisplayComponent);

        // Append warning component to wrapper
        this.renderer.appendChild(this.warningWrapper, this.warningComponentRef.location.nativeElement);

        // Position the warning after a short delay to ensure DOM is ready
        setTimeout(() => this.positionWarning(), 0);
    }

    private positionWarning() {
        if (!this.warningWrapper) return;

        const hostElement = this.elementRef.nativeElement;

        if (this.warningPosition === 'after-label') {
            // Find the label element within app-input-label
            const labelContainer = hostElement.querySelector('app-input-label');
            if (labelContainer) {
                // Try multiple selectors for the label
                const labelElement =
                    labelContainer.querySelector('label') ||
                    labelContainer.querySelector('.k-label') ||
                    labelContainer.querySelector('[for]');

                if (labelElement) {
                    // Add class for inline positioning
                    this.renderer.addClass(this.warningWrapper, 'after-label');

                    // Insert directly after the label text, inside the label element
                    this.renderer.appendChild(labelElement, this.warningWrapper);
                } else {
                    // Fallback: find any element with label text and try to append there
                    const labelTextElement = labelContainer.querySelector(':first-child');
                    if (labelTextElement) {
                        this.renderer.addClass(this.warningWrapper, 'after-label');
                        this.renderer.appendChild(labelTextElement, this.warningWrapper);
                    } else {
                        // Final fallback: add to label container
                        this.renderer.addClass(this.warningWrapper, 'after-label');
                        this.renderer.appendChild(labelContainer, this.warningWrapper);
                    }
                }
            }
        } else {
            // Find the input element - position absolutely
            const inputContainer = hostElement.querySelector('.k-input, kendo-numerictextbox, input, textarea');
            if (inputContainer) {
                // Append to the host element for absolute positioning
                this.renderer.appendChild(hostElement, this.warningWrapper);
            }
        }
    }

    ngOnDestroy() {
        if (this.warningComponentRef) {
            this.warningComponentRef.destroy();
        }
        if (this.warningWrapper && this.warningWrapper.parentElement) {
            this.warningWrapper.parentElement.removeChild(this.warningWrapper);
        }
    }
}
